import React, { useState } from 'react';
import { toast } from 'sonner';
import { ArrowRight, Video } from 'lucide-react';
import { adminApi } from '../api';
import { fmtDate, fmtTime } from '../format';
import s from '../workspace.module.css';
import useHostGroups from './useHostGroups';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select';

const shortLink = (link) => (link || '').replace(/^https?:\/\//, '');

// Hand a booking to another host: same time, same Meet link (the new host becomes a co-host), the old host's event
// and Practice Better session move over. The patient isn't told — their appointment hasn't changed. If the new host
// is already booked then, the dialog says with whom and offers to transfer anyway.
export default function TransferDialog({ booking: b, onClose, onDone }) {
  const hostGroups = useHostGroups();
  const [hostId, setHostId] = useState('');
  const [busy, setBusy] = useState(null);       // the new host's bookings at this time (409 host_busy)
  const [saving, setSaving] = useState(false);
  const host = hostGroups.flatMap((g) => g.items).find((h) => h.id === hostId);
  const patient = [b.patient?.first_name, b.patient?.last_name].filter(Boolean).join(' ') || b.patient?.email || 'This patient';

  const transfer = async (allowOverlap = false) => {
    setSaving(true);
    try {
      const r = await adminApi.post(`/admin/bookings/${b.booking_id}/transfer`, { host_id: hostId, allow_overlap: allowOverlap });
      toast.success(`Transferred to ${r.data.host_name || host?.name}`,
        r.data.pb_status === 'pending' ? { description: 'Practice Better is still catching up. It retries on its own.' } : undefined);
      onDone?.();
      onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail?.code === 'host_busy') setBusy(detail.existing || []);
      else toast.error(typeof detail === 'string' ? detail : (detail?.message || 'Could not transfer the booking'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o && !saving) onClose(); }}>
      <DialogContent className={`${s.formDialog} ${s.lyraDialog}`}>
        <DialogHeader>
          <DialogTitle>Transfer {patient}’s booking</DialogTitle>
          <DialogDescription className={s.sheetDescription}>
            {fmtDate(b.slot_start_utc)} at {fmtTime(b.slot_start_utc)}, now with {b.director_name || b.host_name || 'their host'}.
            Same time and same call. The patient isn’t notified.
          </DialogDescription>
        </DialogHeader>
        <form noValidate onSubmit={(e) => { e.preventDefault(); if (hostId) transfer(Boolean(busy)); }}>
          <div className={`${s.field} ${s.transferField}`}>
            <label htmlFor="transfer-host">New host</label>
            <Select value={hostId || undefined} onValueChange={(v) => { setHostId(v); setBusy(null); }}>
              <SelectTrigger id="transfer-host" aria-label="New host" className={s.select}><SelectValue placeholder="Choose a host" /></SelectTrigger>
              <SelectContent position="popper" className={s.selectMenu}>
                {hostGroups.map((g) => (
                  <SelectGroup key={g.key}>
                    <SelectLabel>{g.label}</SelectLabel>
                    {g.items.filter((h) => h.id !== b.director_id).map((h) => <SelectItem key={h.id} value={h.id} className={s.selectOption}>{h.name}</SelectItem>)}
                  </SelectGroup>
                ))}
              </SelectContent>
            </Select>
          </div>
          {b.meet_link && (
            <p className={s.transferLink}><Video size={13} />Keeps <span className={s.meetLink}>{shortLink(b.meet_link)}</span>{host && <> · {host.name} becomes a co-host</>}</p>
          )}

          {busy && (
            <>
              <p className={s.busyLabel}>{host?.name || 'This host'} is already booked then</p>
              <ul className={s.busyList}>
                {busy.map((x) => (
                  <li key={x.booking_id}>
                    <strong>{x.patient_name}</strong>{x.session_title && <span>· {x.session_title}</span>}
                    {x.meet_link && <span className={s.meetLink}><Video size={13} />{shortLink(x.meet_link)}</span>}
                  </li>
                ))}
              </ul>
              <p className={s.transferNote}>Transfer anyway and {host?.name || 'they'} will have both sessions at once.</p>
            </>
          )}

          <div className={s.dialogActions}>
            <button type="button" className={s.secondaryButton} onClick={onClose} disabled={saving}>Cancel</button>
            <button type="submit" className={s.primaryButton} disabled={saving || !hostId}>
              {saving ? 'Transferring…' : busy ? 'Transfer anyway' : <>Transfer <ArrowRight size={16} /></>}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
